/**
 * **舞台の器**（Issue #522）。1920×1080 の舞台を用意し、入れ物の大きさに合わせて縮めて出す。
 *
 * ここが持つのは **大きさ・縮尺・層の重なり順**だけ。何を描くかは知らない
 * （層は `layers`＝`{ layer, key, node, filter? }` の配列で受け取り、`canvas/layers.js` の宣言順に重ねる）。
 *
 * **時計を持たない**（再生位置・アニメーションはページ側）。同じ入力なら同じ絵になる。
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { orderLayers } from './layers';
import './canvas.css';

/** 舞台の既定の大きさ */
export const CANVAS = Object.freeze({ w: 1920, h: 1080 });

/**
 * 入れ物に収まる縮尺（拡大はしない）。
 *
 * **高さは `maxHeight` から取り、入れ物の高さは見ない** —— 器の高さは中身（縮めた舞台）で決まるので、
 * 入れ物の高さから縮尺を出すと循環する。`maxHeight` を渡さなければ幅だけで決まる。
 */
export function fitScale(box, canvas, maxHeight = null) {
  const w = box?.w > 0 ? box.w : 0;
  if (!(w > 0)) return 1;
  const h = maxHeight > 0 ? maxHeight : canvas.h;
  return Math.min(1, w / canvas.w, h / canvas.h);
}

/**
 * @param filter 舞台全体に当てる CSS の filter（演出の色の調整。Issue #549）。**そのフレームの値**で、時計は持たない
 */
export default function Canvas({ canvas = CANVAS, maxHeight = null, background = '#0b0d10', layers = [], scale: fixedScale = null, onScale = null, filter = '' }) {
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  // 入れ物の大きさを測る（ResizeObserver が無い環境＝テストでは 1 回だけ）
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const read = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => { const el = boxRef.current; if (el) setBox({ w: el.clientWidth, h: el.clientHeight }); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // **縮尺を外へ**（編集の層が、押した座標を舞台のピクセルへ戻すのに要る。Issue #523）
  const onScaleRef = useRef(onScale);
  onScaleRef.current = onScale;
  const scaleNow = fixedScale ?? fitScale(box, canvas, maxHeight);
  useEffect(() => { onScaleRef.current?.(scaleNow); }, [scaleNow]);

  const scale = fixedScale ?? fitScale(box, canvas, maxHeight);
  const ordered = orderLayers(layers);
  return (
    <div className="de-canvas" ref={boxRef}>
      {/*
        **縮めたあとの大きさの箱**（`fit`）を挟む。舞台そのものは 1920×1080 のままで `transform: scale()` を
        掛けるので、**レイアウト上の箱は縮まない** —— これを直接 flex で中央寄せすると、箱が入れ物より
        広いときに左側がはみ出して切られる（舞台の端しか見えなくなる）。
      */}
      <div className="de-canvas__fit" data-testid="de-canvas-fit" style={{ width: canvas.w * scale, height: canvas.h * scale }}>
        <div
          className="de-canvas__stage"
          data-testid="de-canvas"
          style={{ width: canvas.w, height: canvas.h, transform: `scale(${scale})`, background, filter: filter || undefined }}
        >
          {/* 層ごとの filter（`filter`。盤の側だけ白黒にするなど。Issue #604） */}
          {ordered.map(({ key, node, filter: f }) => <div className="de-canvas__slot" key={key} style={f ? { filter: f } : undefined}>{node}</div>)}
        </div>
      </div>
    </div>
  );
}
